import { SnackbarItem } from '@george-labs.com/design-system';
import { useCallback } from 'react';
import type { SNACKBAR_ITEM_AUTO_CLOSEABLE } from '@george-labs.com/design-system/types/components/Snackbar';

import type { SnackbarButtonProps } from './SnackItem';
import { displayMessage } from './NotificationsService';
import { useNotificationsState } from './useNotificationsState';

type HideNotification = () => void;

type NotificationFn = (
    message: string,
    buttonProps?: SnackbarButtonProps,
    autoClosable?: SNACKBAR_ITEM_AUTO_CLOSEABLE,
) => HideNotification;

type UseNotifications = {
    displaySuccess: NotificationFn;
    displayError: NotificationFn;
    displayInfo: NotificationFn;
    displayBusy: NotificationFn;
};

export const useNotifications = (contextName: string): UseNotifications => {
    const {
        dispatchActions: { addItem, removeItem },
    } = useNotificationsState();

    const displayError = useCallback<NotificationFn>(
        (message, buttonProps, autoClosable) =>
            displayMessage(
                addItem,
                removeItem,
                `${contextName}-error-message`,
                message,
                SnackbarItem.VARIANT.ERROR,
                buttonProps,
                autoClosable,
            ),
        [addItem, removeItem, contextName],
    );

    const displayInfo = useCallback<NotificationFn>(
        (message, buttonProps, autoClosable) =>
            displayMessage(
                addItem,
                removeItem,
                `${contextName}-info-message`,
                message,
                SnackbarItem.VARIANT.DEFAULT,
                buttonProps,
                autoClosable,
            ),
        [addItem, removeItem, contextName],
    );

    const displaySuccess = useCallback<NotificationFn>(
        (message, buttonProps, autoClosable) =>
            displayMessage(
                addItem,
                removeItem,
                `${contextName}-success-message`,
                message,
                SnackbarItem.VARIANT.SUCCESS,
                buttonProps,
                autoClosable,
            ),
        [addItem, removeItem, contextName],
    );

    const displayBusy = useCallback<NotificationFn>(
        (message, buttonProps, autoClosable) =>
            displayMessage(
                addItem,
                removeItem,
                `${contextName}-busy-message`,
                message,
                SnackbarItem.VARIANT.BUSY,
                buttonProps,
                autoClosable,
            ),
        [addItem, removeItem, contextName],
    );

    return { displaySuccess, displayError, displayInfo, displayBusy };
};
